import { Dispatch, SetStateAction, useContext, useState } from 'react'
import Visibility from '@mui/icons-material/Visibility'
import VisibilityOff from '@mui/icons-material/VisibilityOff'
import { Button, FormControl, Grid, IconButton, Typography } from '@mui/material'
import { Form, Formik, FormikHelpers, FormikProps } from 'formik'
import { useSnackbar } from 'notistack'
import * as Yup from 'yup'

import FormikTextField from '../../components/formik-input'
import SubmitContainer from '../../components/submit-container'
import { AuthenticationContext } from '../../providers/auth-provider'
import Api from '../../utils/api'
import useSharedStyles from '../../utils/shared.styles'

import useStyles from './index.styles'

interface IProps {
  setShowPasswordForm: Dispatch<SetStateAction<boolean>>
  email: string
  setEmail: Dispatch<SetStateAction<string>>
}

interface IFormValues {
  email: string
  password: string
}

const EmailPasswordForm = (props: IProps) => {
  const { setShowPasswordForm, email, setEmail } = props

  const classes = useStyles
  const sharedClasses = useSharedStyles

  const { setIsAuthenticated } = useContext(AuthenticationContext)

  const { enqueueSnackbar } = useSnackbar()

  const [showPassword, setShowPassword] = useState<boolean>(false)

  const initialValues = {
    email,
    password: '',
  }

  const schema = Yup.object().shape({
    email: Yup.string().email('Please provide a valid email address').required('Email is required'),
    password: Yup.string().required('Password is required'),
  })

  const handleSubmit = async (values: IFormValues, actions: FormikHelpers<IFormValues>) => {
    const res = await Api.post('/api/login', {
      username: values.email,
      password: values.password,
    })

    if (!res.ok) {
      actions.resetForm()
      enqueueSnackbar('Login Failed', { variant: 'error' })
    } else {
      setIsAuthenticated(true)
    }
  }

  const endAdornment = (
    <IconButton aria-label="toggle password visibility" onClick={() => setShowPassword(!showPassword)} data-testid="show-password">
      {showPassword ? <VisibilityOff /> : <Visibility />}
    </IconButton>
  )

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} validationSchema={schema} validateOnMount={true} enableReinitialize>
      {({ isSubmitting, values }: FormikProps<IFormValues>) => (
        <Form>
          <Grid container={true} spacing={1} sx={classes.formContainer}>
            <Grid container={true} size={12} sx={classes.formFieldContainer}>
              <FormControl sx={classes.formControl}>
                <FormikTextField
                  name="email"
                  label="Email"
                  placeholder="Enter your email address"
                  className="fs-exclude"
                  sx={sharedClasses.input}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                />
              </FormControl>
            </Grid>

            <Grid container={true} size={12} sx={classes.formFieldContainer}>
              <FormControl sx={classes.formControl}>
                <FormikTextField
                  name="password"
                  label="Password"
                  className="fs-exclude"
                  sx={sharedClasses.input}
                  type={showPassword ? 'text' : 'password'}
                  endAdornment={endAdornment}
                />
              </FormControl>
            </Grid>

            <SubmitContainer>
              <Button type="submit" loading={isSubmitting} disabled={isSubmitting} variant="contained" sx={classes.loginFormSubmitButton}>
                Log In
              </Button>

              <Typography variant="body2">OR</Typography>

              <Button
                type="button"
                variant="text"
                onClick={() => {
                  setEmail(values.email)
                  setShowPasswordForm(false)
                }}
              >
                Get Verification Code
              </Button>
            </SubmitContainer>
          </Grid>
        </Form>
      )}
    </Formik>
  )
}

export default EmailPasswordForm
